import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Clock,
  ClipboardList,
  Send,
  Infinity as InfinityIcon
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, subWeeks, addWeeks } from 'date-fns';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import PeriodSelector from '../components/PeriodSelector';
import ConsolidatedTeamSummary from '../components/ConsolidatedTeamSummary';
import ExportPdfButton from '../components/ExportPdfButton';
import {
  buildWeeklyUpdatePayload,
  formatProgressPercent,
  getNumericalProgressText,
  isNumericalTask,
  isOngoingTask,
  isProgressEditable,
} from '../lib/taskTypes';

const WeeklySyncPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  // 'week' keeps the detailed weekly sync; month/quarter/year show consolidated summaries.
  const [period, setPeriod] = useState('week');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [activeTab, setActiveTab] = useState('my-updates');
  const [myTasks, setMyTasks] = useState([]);
  const [myTasksLoading, setMyTasksLoading] = useState(false);
  const [localUpdates, setLocalUpdates] = useState({});
  const [submittingAll, setSubmittingAll] = useState(false);
  const [missingUpdates, setMissingUpdates] = useState(null);
  const [overdueTasks, setOverdueTasks] = useState([]);

  useEffect(() => {
    if (period === 'week') fetchData();
  }, [weekOffset, period]);

  useEffect(() => {
    if (period === 'week') return undefined;
    let cancelled = false;
    setSummaryLoading(true);
    api.get('/progress/summary', { params: { period, offset: weekOffset } })
      .then(res => { if (!cancelled) setSummary(res.data); })
      .catch(() => { if (!cancelled) { setSummary(null); toast.error('Failed to load summary'); } })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [period, weekOffset]);

  useEffect(() => {
    if (activeTab === 'my-updates') {
      fetchMyTasks();
    }
  }, [activeTab]);

  const fetchAllOverdueTasks = async () => {
    const limit = 200;
    let page = 1;
    let pages = 1;
    const allTasks = [];

    while (page <= pages) {
      const res = await api.get(`/tasks?overdue=true&limit=${limit}&page=${page}`);
      const tasks = res.data?.tasks || [];
      allTasks.push(...tasks);
      pages = res.data?.pagination?.pages || 1;
      page += 1;
    }

    return allTasks;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [progressRes, snapshotRes, missingRes, overdueRes] = await Promise.all([
        api.get(`/progress/weekly?week_offset=${weekOffset}`),
        api.get('/progress/snapshot'),
        api.get(`/progress/team-missing-updates?week_offset=${weekOffset}`).catch(() => ({ data: null })),
        fetchAllOverdueTasks().catch(() => [])
      ]);
      setProgress(progressRes.data);
      setSnapshot(snapshotRes.data);
      setMissingUpdates(missingRes.data || missingRes || null);
      setOverdueTasks(Array.isArray(overdueRes) ? overdueRes : []);
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const fetchMyTasks = async () => {
    setMyTasksLoading(true);
    try {
      const res = await api.get('/weekly-updates/my-tasks');
      const tasks = res.data?.tasks || [];
      setMyTasks(tasks);
      const initial = {};
      for (const t of tasks) {
        initial[t.id] = {
          ...(!isNumericalTask(t) && !isOngoingTask(t) ? {
            percent_complete: t.weekly_update?.percent_complete ?? t.percent_complete ?? 0,
          } : {}),
          notes: t.weekly_update?.notes ?? '',
          blockers: t.weekly_update?.blockers ?? '',
        };
      }
      setLocalUpdates(initial);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setMyTasksLoading(false);
    }
  };

  const handleSubmitAllUpdates = async () => {
    setSubmittingAll(true);
    try {
      const updates = Object.entries(localUpdates).map(([taskId, data]) => {
        const task = myTasks.find(item => String(item.id) === String(taskId));
        return buildWeeklyUpdatePayload(task ? task : { id: taskId, task_type: 'numerical_target' }, data);
      });
      await api.post('/weekly-updates/bulk', { updates });
      toast.success('All updates submitted');
      fetchMyTasks();
    } catch (error) {
      toast.error('Failed to submit updates');
    } finally {
      setSubmittingAll(false);
    }
  };

  const summaryStats = useMemo(() => {
    const members = progress?.progress || [];
    if (members.length === 0) {
      return {
        totalHours: 0,
        avgHoursPerPerson: 0,
        teamCompletionRate: 0,
        membersComplete: 0,
        totalMembers: 0,
      };
    }

    const totalHours = members.reduce((sum, m) => sum + m.total_hours, 0);
    const totalTasks = members.reduce((sum, m) => sum + m.tasks_completed + m.tasks_pending, 0);
    const totalCompleted = members.reduce((sum, m) => sum + m.tasks_completed, 0);
    const membersComplete = members.filter(m => m.missed_days === 0).length;

    return {
      totalHours,
      avgHoursPerPerson: members.length > 0 ? totalHours / members.length : 0,
      teamCompletionRate: totalTasks > 0 ? (totalCompleted / totalTasks) * 100 : 0,
      membersComplete,
      totalMembers: members.length,
    };
  }, [progress]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'warning';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const overdueTasksByUser = useMemo(() => {
    const grouped = {};

    for (const task of overdueTasks) {
      const assigneeIds = Array.isArray(task.assigned_to) ? task.assigned_to : [];
      const assigneeNames = Array.isArray(task.assigned_names) ? task.assigned_names : [];

      assigneeIds.forEach((assigneeId, index) => {
        if (!grouped[assigneeId]) {
          grouped[assigneeId] = {
            user_name: assigneeNames[index] || 'Unknown User',
            tasks: [],
          };
        }

        const alreadyIncluded = grouped[assigneeId].tasks.some(existing => existing.id === task.id);
        if (!alreadyIncluded) {
          grouped[assigneeId].tasks.push(task);
        }
      });
    }

    Object.values(grouped).forEach((entry) => {
      entry.tasks.sort((a, b) => {
        const aDate = new Date(a.end_date || a.deadline || 0).getTime();
        const bDate = new Date(b.end_date || b.deadline || 0).getTime();
        return aDate - bDate;
      });
    });

    return grouped;
  }, [overdueTasks]);

  const uniqueOverdueTaskCount = useMemo(() => {
    const uniqueTaskIds = new Set();
    for (const task of overdueTasks) {
      if (task?.id) {
        uniqueTaskIds.add(task.id);
      }
    }
    return uniqueTaskIds.size;
  }, [overdueTasks]);

  const teamOverdueCount = snapshot?.overdue_tasks ?? uniqueOverdueTaskCount;

  if (loading && period === 'week') {
    return (
      <div className="flex items-center justify-center h-64" data-testid="sync-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasProgressData = progress?.progress && progress.progress.length > 0;

  // Server-generated PDF for the active tab, used by Export PDF.
  const teamReport = {
    path: '/progress/summary/pdf',
    params: { period, offset: weekOffset },
    filename: `syncly-team-progress-${period}-${(period === 'week' ? progress?.week_start : summary?.start_date) || 'report'}.pdf`,
  };
  const myUpdatesReport = {
    path: '/weekly-updates/my-tasks/pdf',
    filename: `syncly-weekly-updates-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
  };

  const weekLabel = progress?.week_start && progress?.week_end
    ? `${format(new Date(progress.week_start + 'T00:00:00'), 'MMM d')} - ${format(new Date(progress.week_end + 'T00:00:00'), 'MMM d, yyyy')}`
    : '';

  return (
    <div className="space-y-6" data-testid="weekly-sync-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight uppercase">WEEKLY SYNC</h1>
          <p className="text-muted-foreground text-sm">
            Team progress overview
          </p>
        </div>
        <ExportPdfButton
          api={api}
          {...(activeTab === 'team-overview' ? teamReport : myUpdatesReport)}
          testId={activeTab === 'team-overview' ? 'export-pdf-btn' : 'my-updates-export-pdf-btn'}
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="my-updates">MY UPDATES</TabsTrigger>
          <TabsTrigger value="team-overview">TEAM OVERVIEW</TabsTrigger>
        </TabsList>

        <TabsContent value="my-updates" className="space-y-4">
          {myTasksLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : myTasks.length > 0 ? (
            <>
              <div className="space-y-3">
                {myTasks.map(task => (
                  <Card key={task.id} className="border-border">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-medium truncate">{task.title}</h3>
                          {isNumericalTask(task) && (
                            <p className="text-xs text-muted-foreground mt-0.5">{getNumericalProgressText(task)}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {isNumericalTask(task) && <Badge variant="outline" className="text-[9px]">NUMERICAL</Badge>}
                          {isOngoingTask(task) && <Badge variant="outline" className="text-[9px] border-primary/40 text-primary">ONGOING</Badge>}
                          <Badge variant={task.status === 'completed' ? 'success' : task.status === 'in_progress' ? 'default' : 'secondary'} className="text-[10px]">
                            {task.status.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">{isOngoingTask(task) ? 'PROGRESS' : isProgressEditable(task) ? 'PERCENT COMPLETE' : 'CALCULATED PROGRESS'}</Label>
                          {isOngoingTask(task) ? (
                            <div className="h-10 flex items-center gap-2 border border-input bg-muted px-3 text-sm text-muted-foreground" data-testid={`ongoing-progress-${task.id}`}>
                              <InfinityIcon className="h-4 w-4 shrink-0" />
                              <span className="truncate">Ongoing{task.end_date ? ` · till ${format(new Date(task.end_date), 'MMM d, yyyy')}` : ''}</span>
                            </div>
                          ) : isProgressEditable(task) ? (
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={localUpdates[task.id]?.percent_complete ?? ''}
                              onChange={(e) => setLocalUpdates(prev => ({
                                ...prev,
                                [task.id]: { ...prev[task.id], percent_complete: e.target.value === '' ? '' : Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }
                              }))}
                            />
                          ) : (
                            <div className="h-10 flex items-center border border-input bg-muted px-3 text-sm" data-testid={`calculated-progress-${task.id}`}>
                              {formatProgressPercent(task.percent_complete)}%
                            </div>
                          )}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">NOTES</Label>
                          <Textarea
                            value={localUpdates[task.id]?.notes ?? ''}
                            onChange={(e) => setLocalUpdates(prev => ({
                              ...prev,
                              [task.id]: { ...prev[task.id], notes: e.target.value }
                            }))}
                            placeholder="Progress notes..."
                            rows={1}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">BLOCKERS</Label>
                          <Textarea
                            value={localUpdates[task.id]?.blockers ?? ''}
                            onChange={(e) => setLocalUpdates(prev => ({
                              ...prev,
                              [task.id]: { ...prev[task.id], blockers: e.target.value }
                            }))}
                            placeholder="Any blockers..."
                            rows={1}
                          />
                        </div>
                      </div>
                      {task.weekly_update && (
                        <p className="text-xs text-green-500">Last updated: {new Date(task.weekly_update.updatedAt).toLocaleString()}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Button onClick={handleSubmitAllUpdates} disabled={submittingAll} className="w-full">
                {submittingAll ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                SUBMIT ALL UPDATES
              </Button>
            </>
          ) : (
            <Card className="border-border">
              <CardContent className="p-12 text-center">
                <ClipboardList className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium">No pending tasks this week</h3>
                <p className="text-sm text-muted-foreground mt-1">You're all caught up! No tasks need weekly updates right now.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="team-overview" className="space-y-6">
      {/* Overall Snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-border hover:border-primary/50 transition-colors">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">TOTAL TASKS</p>
            <p className="text-2xl md:text-3xl font-bold">{snapshot?.total_tasks || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-border hover:border-primary/50 transition-colors">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">COMPLETED</p>
            <p className="text-2xl md:text-3xl font-bold text-green-500">{snapshot?.completed_tasks || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-border hover:border-primary/50 transition-colors">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">IN PROGRESS</p>
            <p className="text-2xl md:text-3xl font-bold text-primary">{snapshot?.in_progress_tasks || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-border hover:border-primary/50 transition-colors">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">PENDING</p>
            <p className="text-2xl md:text-3xl font-bold">{snapshot?.pending_tasks || 0}</p>
          </CardContent>
        </Card>
        <Card className={`col-span-2 md:col-span-1 border-border hover:border-primary/50 transition-colors ${teamOverdueCount > 0 ? 'border-destructive' : ''}`}>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">OVERDUE</p>
            <p className={`text-2xl md:text-3xl font-bold ${teamOverdueCount > 0 ? 'text-destructive' : ''}`}>
              {teamOverdueCount}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Period Navigation */}
      <PeriodSelector
        period={period}
        onPeriodChange={setPeriod}
        offset={weekOffset}
        onOffsetChange={setWeekOffset}
        caption={period === 'week' ? 'WEEKLY SYNC' : 'CONSOLIDATED SUMMARY'}
        label={period === 'week' ? weekLabel : (summary?.label || '…')}
        testIdPrefix="sync-period"
      />

      {period !== 'week' ? (
        <ConsolidatedTeamSummary summary={summary} loading={summaryLoading} />
      ) : (
      <>

      {/* Summary Stats Bar */}
      {hasProgressData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4" data-testid="summary-stats-bar">
          <Card className="border-border bg-muted/30">
            <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="h-10 w-10 bg-primary/10 hidden sm:flex items-center justify-center shrink-0">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold">{summaryStats.totalHours.toFixed(1)}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">Team Hours</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-muted/30">
            <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="h-10 w-10 bg-primary/10 hidden sm:flex items-center justify-center shrink-0">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold">{summaryStats.avgHoursPerPerson.toFixed(1)}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">Hrs/Person</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-muted/30">
            <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="h-10 w-10 bg-green-500/10 hidden sm:flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold">{Math.round(summaryStats.teamCompletionRate)}%</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">Completion</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-muted/30">
            <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className={`h-10 w-10 hidden sm:flex items-center justify-center shrink-0 ${summaryStats.membersComplete === summaryStats.totalMembers ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}>
                {summaryStats.membersComplete === summaryStats.totalMembers ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold">{summaryStats.membersComplete}/{summaryStats.totalMembers}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">Timesheets</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Progress */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            TEAM PROGRESS
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasProgressData ? (
            <div className="space-y-4">
              {progress.progress.map(member => {
                const totalTasks = member.tasks_completed + member.tasks_pending;
                const completionPct = totalTasks > 0
                  ? Math.round((member.tasks_completed / totalTasks) * 100)
                  : 0;
                const memberOverdueTasks = overdueTasksByUser[member.user_id]?.tasks || [];

                return (
                  <div
                    key={member.user_id}
                    className="p-4 border border-border hover:border-primary/50 transition-colors"
                    data-testid={`sync-member-${member.user_id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">
                          {member.user_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium">{member.user_name}</p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {member.total_hours.toFixed(1)}h logged
                            </span>
                            <span className="hidden sm:inline text-muted-foreground/50">|</span>
                            <span>{member.days_logged} day{member.days_logged !== 1 ? 's' : ''}</span>
                          </div>
                          {/* Per-person missed days detail */}
                          {member.missed_days > 0 && (
                            <p className="text-xs font-semibold text-orange-500 mt-1" data-testid={`sync-member-missed-${member.user_id}`}>
                              <AlertTriangle className="h-3 w-3 inline-block mr-1 -mt-px" />
                              {member.missed_days} day{member.missed_days !== 1 ? 's' : ''} missing
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                        <div className="text-center">
                          <p className="text-base sm:text-lg font-bold text-green-500">{member.tasks_completed}</p>
                          <p className="text-[10px] text-muted-foreground uppercase">Done</p>
                        </div>
                        <div className="text-center">
                          <p className={`text-base sm:text-lg font-bold ${memberOverdueTasks.length > 0 ? 'text-destructive' : ''}`}>{memberOverdueTasks.length}</p>
                          <p className="text-[10px] text-muted-foreground uppercase">Overdue</p>
                        </div>
                        {member.tasks_ongoing > 0 && (
                          <div className="text-center">
                            <p className="text-base sm:text-lg font-bold text-primary">{member.tasks_ongoing}</p>
                            <p className="text-[10px] text-muted-foreground uppercase">Ongoing</p>
                          </div>
                        )}
                        <div className="text-center">
                          {member.missed_days === 0 ? (
                            <div className="flex items-center gap-1 text-green-500">
                              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" />
                              <span className="text-[10px] uppercase">Complete</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-orange-500">
                              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
                              <span className="text-[10px] uppercase font-semibold">{member.missed_days} Missing</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="h-2 bg-muted overflow-hidden">
                        <div
                          className="h-full bg-green-500 transition-all"
                          style={{ width: `${completionPct}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {completionPct}% completion rate
                      </p>
                    </div>

                    {/* Overdue Tasks (user-wise) */}
                    {memberOverdueTasks.length > 0 && (
                      <div className="mt-4 space-y-2" data-testid={`sync-member-overdue-list-${member.user_id}`}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-destructive">
                          Overdue Tasks ({memberOverdueTasks.length})
                        </p>
                        {memberOverdueTasks.map(task => (
                          <div
                            key={`${member.user_id}-${task.id}`}
                            className="border-l-4 border-l-destructive border-destructive/50 bg-destructive/5 p-3 cursor-pointer hover:border-destructive/70"
                            onClick={() => navigate(`/tasks/${task.id}`)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(`/tasks/${task.id}`);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            data-testid={`sync-member-overdue-task-${member.user_id}-${task.id}`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm font-semibold text-foreground">{task.title}</p>
                              <div className="flex items-center gap-1">
                                <Badge variant={getPriorityColor(task.priority)} className="text-[10px]">
                                  {(task.priority || 'medium').toUpperCase()}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="text-[10px] gap-1 border-destructive/40 bg-destructive/10 text-destructive font-semibold"
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  OVERDUE
                                </Badge>
                              </div>
                            </div>
                            {task.description && (
                              <p className="text-xs text-foreground/85 mt-1 line-clamp-2">{task.description}</p>
                            )}
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                              <span className="flex items-center gap-1 text-destructive font-medium">
                                <Clock className="h-3 w-3" />
                                {task.end_date ? format(new Date(task.end_date), 'MMM d, yyyy') : 'No due date'}
                              </span>
                              <span className="text-muted-foreground">•</span>
                              <span className="text-muted-foreground">{task.percent_complete || 0}% complete</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Missing Task Updates */}
                    {(() => {
                      const memberMissing = missingUpdates?.members?.find(m => m.user_id === member.user_id);
                      if (memberMissing && memberMissing.missing_tasks && memberMissing.missing_tasks.length > 0) {
                        return (
                          <div className="mt-3 p-3 bg-amber-500/5 border border-amber-500/20">
                            <p className="text-xs text-amber-600 font-medium">{memberMissing.missing_tasks.length} missing update{memberMissing.missing_tasks.length !== 1 ? 's' : ''}:</p>
                            <ul className="text-xs text-muted-foreground ml-2 mt-1">
                              {memberMissing.missing_tasks.map(t => (
                                <li key={t.id}>&#8226; {t.title}</li>
                              ))}
                            </ul>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                );
              })}
            </div>
          ) : (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="sync-empty-state">
              <div className="h-16 w-16 bg-muted flex items-center justify-center mb-4">
                <TrendingUp className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-1">No progress data available</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                There is no team progress data for this week. This could mean no tasks have been assigned or no timesheets have been submitted yet.
              </p>
              {weekOffset > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => setWeekOffset(0)}
                >
                  Go to current week
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Week Summary Stats */}
      {hasProgressData && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
              WEEK SUMMARY
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 border border-border">
                <p className="text-2xl font-bold">
                  {summaryStats.totalHours.toFixed(1)}
                </p>
                <p className="text-xs text-muted-foreground uppercase">Total Hours</p>
              </div>
              <div className="p-4 border border-border">
                <p className="text-2xl font-bold">
                  {progress.progress.reduce((sum, m) => sum + m.tasks_completed, 0)}
                </p>
                <p className="text-xs text-muted-foreground uppercase">Tasks Completed</p>
              </div>
              <div className="p-4 border border-border">
                <p className="text-2xl font-bold">
                  {summaryStats.membersComplete}/{summaryStats.totalMembers}
                </p>
                <p className="text-xs text-muted-foreground uppercase">Timesheets Complete</p>
              </div>
              <div className="p-4 border border-border">
                <p className="text-2xl font-bold">
                  {summaryStats.avgHoursPerPerson.toFixed(1)}
                </p>
                <p className="text-xs text-muted-foreground uppercase">Avg Hours/Person</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WeeklySyncPage;
